// Cart GWP auto-add: puts earned gift lines in the cart and takes them out again when
// the cart drops below their amount, driven by the config `cart-gwp-gift.liquid`
// renders. Loaded from scripts.liquid, not inline: the drawer morphs the cart in, and
// a morphed <script> never runs (#364).
(function () {
  if (window.__cartGwpGiftInit) return;
  window.__cartGwpGiftInit = true;

  const setCheckoutLocked = (locked) => {
    document.querySelectorAll('[name="checkout"]').forEach((btn) => {
      btn.disabled = locked;
      btn.setAttribute('aria-busy', String(locked));
    });
    document.querySelectorAll('.additional-checkout-buttons').forEach((el) => {
      el.setAttribute('aria-busy', String(locked));
    });
  };

  const insertPendingRow = (text) => {
    if (document.querySelector('[data-cart-gwp-pending]')) return;
    const tbody = document.querySelector('cart-items-component tbody[role="rowgroup"]')
      || document.querySelector('cart-items-component tbody');
    if (!tbody) return;
    const row = document.createElement('tr');
    row.className = 'cart-items__table-row cart-items__free-tester-pending';
    row.dataset.cartGwpPending = 'true';
    // role=status so the "Adding your gift…" text is announced, not just shown.
    row.setAttribute('role', 'status');
    row.innerHTML =
      '<td class="cart-items__media">' +
        '<span class="cart-items__free-tester-pending-spinner" aria-hidden="true"></span>' +
      '</td>' +
      '<td class="cart-items__details" colspan="3">' +
        '<p class="cart-items__title"></p>' +
      '</td>';
    row.querySelector('.cart-items__title').textContent = text;
    tbody.appendChild(row);
  };

  // Normally the server re-render replaces the tbody and takes the spinner with
  // it; this clears it by hand when a sync fails and no re-render follows.
  const removePendingRow = () => {
    document.querySelectorAll('[data-cart-gwp-pending]').forEach((el) => el.remove());
  };

  const syncGifts = async () => {
    if (window.__cartGwpGiftBusy) return;
    const configEl = document.querySelector('[data-cart-gwp-gift-config]');
    if (!configEl) return;

    let cfg;
    try {
      cfg = JSON.parse(configEl.textContent);
    } catch (_) { return; }

    const wanted = cfg.wanted || [];
    const renderedPresent = cfg.present || [];
    if (!wanted.length && !renderedPresent.length) return;

    window.__cartGwpGiftBusy = true;
    setCheckoutLocked(true);
    try {
      // Section responses can predate a completed gift add or scent swap.
      const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!cartRes.ok) throw new Error('Cart lookup failed');
      const live = await cartRes.json();
      const present = live.items
        .filter((item) => item.properties?._gwp)
        .map((item) => ({ tier: item.properties._gwp_tier, key: item.key, quantity: item.quantity }));
      const presentByTier = new Map();
      present.forEach((p) => {
        const list = presentByTier.get(p.tier) || [];
        list.push(p);
        presentByTier.set(p.tier, list);
      });

      const wantedTiers = new Set(wanted.map((w) => w.tier));
      const toAdd = wanted.filter((w) => !presentByTier.has(w.tier));
      const liveKeys = new Set(live.items.map((item) => item.key));
      const changes = [];
      // Only remove unearned lines identified by this render. A newly arrived
      // tier may have been earned by a cart change newer than our configuration.
      renderedPresent.forEach((p) => {
        if (!wantedTiers.has(p.tier) && liveKeys.has(p.key)) {
          changes.push({ id: p.key, quantity: 0 });
        }
      });
      wantedTiers.forEach((tier) => {
        const lines = presentByTier.get(tier) || [];
        lines.forEach((line, idx) => {
          const quantity = idx === 0 ? 1 : 0;
          if (line.quantity !== quantity) changes.push({ id: line.key, quantity });
        });
      });

      const renderedByKey = new Map(renderedPresent.map((p) => [p.key, p]));
      const staleRender = present.length !== renderedPresent.length || present.some((p) => {
        const rendered = renderedByKey.get(p.key);
        return !rendered || rendered.tier !== p.tier || rendered.quantity !== p.quantity;
      });
      if (!toAdd.length && !changes.length && !staleRender) return;

      if (toAdd.length) {
        insertPendingRow(cfg.addingText || 'Adding your gift…');
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: toAdd.map((w) => ({
              id: w.variantId,
              quantity: 1,
              properties: { _gwp: 'true', _gwp_tier: w.tier },
            })),
          }),
        });
        // A gift can sell out between the server render and this call; the whole
        // batch then 422s. Bail without dispatching a success so we don't retry
        // on a false premise — the next real render refreshes the config.
        if (!addRes.ok) {
          removePendingRow();
          return;
        }
      }

      for (const change of changes) {
        const changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(change),
        });
        if (!changeRes.ok) throw new Error('Gift quantity change failed');
      }

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { sourceId: 'cart-gwp-gift', data: {} },
        })
      );
    } catch (err) {
      console.error('[cart-gwp-gift] sync failed', err);
      removePendingRow();
    } finally {
      window.__cartGwpGiftBusy = false;
      setCheckoutLocked(false);
    }
  };

  document.addEventListener('cart:update', (e) => {
    if (e.detail && e.detail.sourceId === 'cart-gwp-gift') return;
    setTimeout(syncGifts, 50);
  });

  // Scent picker on the gift line: swap the sample's variant while keeping the
  // gift properties, so syncGifts sees the same tier and leaves the line alone.
  // Dispatched with sourceId 'cart-gwp-gift' for the same reason — no reconcile.
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.classList.contains('cart-items__gwp-scent')) return;

    const newVariantId = parseInt(select.value, 10);
    const currentVariantId = parseInt(select.dataset.currentVariant, 10);
    if (!newVariantId || newVariantId === currentVariantId) return;

    const itemKey = select.dataset.itemKey;
    const itemQty = parseInt(select.dataset.itemQty, 10) || 1;
    const tier = select.dataset.gwpTier || '';
    if (!itemKey) return;

    // Take the same lock syncGifts uses, so a cart:update mid-swap can't run the
    // reconciler against a cart that momentarily holds both the old and new line
    // (which would strand an unearned gift). Cleared in .finally below.
    window.__cartGwpGiftBusy = true;
    select.disabled = true;
    const row = select.closest('tr[data-key]');
    if (row) row.classList.add('cart-items-disabled');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    // Add the chosen scent first (gift props preserved), then drop the old line —
    // if the add fails, the current sample stays in the cart.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: newVariantId, quantity: itemQty, properties: { _gwp: 'true', _gwp_tier: tier } }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('Add failed');
        const removeBody = { id: itemKey, quantity: 0 };
        if (sectionIds.length) {
          removeBody.sections = sectionIds.join(',');
          removeBody.sections_url = window.location.pathname;
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(removeBody),
        });
      })
      .then((r) => {
        if (!r.ok) throw new Error('Remove failed');
        return r.json();
      })
      .then((data) => {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { sourceId: 'cart-gwp-gift', resource: data, data: { sections: data.sections || {} } },
        }));
      })
      .catch((err) => {
        console.error('[cart-gwp-gift] scent change failed', err);
        select.disabled = false;
        select.value = currentVariantId;
        if (row) row.classList.remove('cart-items-disabled');
      })
      .finally(() => {
        window.__cartGwpGiftBusy = false;
      });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGifts);
  } else {
    syncGifts();
  }
})();
